import { useCallback, useMemo, useState } from 'react';

import { useArtifactLineage } from '@/components/agent-chat';
import { ChatRailHeader } from '@/components/chat-workspace/chat-rail-header';
import type { ChatWorkspaceRailContext } from '@/components/chat-workspace/chat-workspace';
import type { SurfaceDescriptor } from '@/components/surface-host/surface-host';
import { useAgentConversations } from '@/hooks/queries/agents/use-agent-conversations';
import { useSurfaceConversation } from '@/hooks/queries/agents/use-surface-conversation';
import { useCanvasApplyMutationsBridgeStore } from '@/stores/canvas-apply-mutations-bridge-store';

export const APPLY_MUTATIONS_UNAVAILABLE_REASON =
  'Open the workflow to apply this change';

export function useCanvasMutationBridgeState() {
  const bridge = useCanvasApplyMutationsBridgeStore((state) => state.bridge);

  return useMemo(
    () => ({
      isMountedCanvasLocked: bridge?.disabled ?? false,
      applyMutationsDisabled: !bridge || bridge.disabled,
      applyMutationsDisabledReason: bridge
        ? bridge.disabledReason
        : APPLY_MUTATIONS_UNAVAILABLE_REASON,
    }),
    [bridge],
  );
}

interface SurfaceChatHostOptions {
  agentId: string;
  resumeSurface: SurfaceDescriptor;
  setSurface: (surface: SurfaceDescriptor | null) => void;
}

/**
 * Shared wiring for product routes that anchor chat to one surface.
 *
 * The host still owns its anchor and layering rules. This hook owns the
 * identical conversation-resume state, artifact lineage, mutation bridge,
 * layered-surface close action, and simple rail header used by those routes.
 */
export function useSurfaceChatHost({
  agentId,
  resumeSurface,
  setSurface,
}: SurfaceChatHostOptions) {
  const { data: conversations = [], refetch: refetchConversations } =
    useAgentConversations(agentId);
  const {
    conversationId: startingConversationId,
    isResolving: isResolvingSurfaceConversation,
  } = useSurfaceConversation(resumeSurface, agentId);

  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [startedNewConversation, setStartedNewConversation] = useState(false);
  const conversationId =
    selectedConversationId ??
    (startedNewConversation ? null : startingConversationId);
  const conversationLinkResolving =
    !selectedConversationId &&
    !startedNewConversation &&
    isResolvingSurfaceConversation;

  const artifactLineage = useArtifactLineage(conversationId);
  const mutationBridge = useCanvasMutationBridgeState();
  const closeLayeredSurface = useCallback(() => setSurface(null), [setSurface]);

  const renderRailHeader = useCallback(
    ({ onCollapse }: ChatWorkspaceRailContext) => (
      <ChatRailHeader
        conversations={conversations}
        conversationId={conversationId}
        onNewConversation={() => {
          setSelectedConversationId(null);
          setStartedNewConversation(true);
        }}
        onSelectConversation={(id) => {
          setStartedNewConversation(false);
          setSelectedConversationId(id);
        }}
        onCollapse={onCollapse}
      />
    ),
    [conversations, conversationId],
  );

  return {
    conversationId,
    conversationLinkResolving,
    onConversationChange: setSelectedConversationId,
    onTurnComplete: refetchConversations,
    artifactLineage,
    closeLayeredSurface,
    renderRailHeader,
    ...mutationBridge,
  };
}
